import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Autocomplete } from '@mui/lab';
import { Box, FormControl, Grid, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import { Translate, withLocalize, LocalizeContextProps } from 'react-localize-redux';
import { ButtonAdd } from '../../../components/general/mini_components';
import { ReduxStore, TYPE_BILL_ITEM } from '../../../constants/types';
import { Billable, BillableCombo } from './types';
import { getBillableComboAction, pushBillables } from '../../../redux/actions/billingActions';
import PrestationSelector from './PrestationSelector';

export enum BillItemAddType {
    ELEMENT = 'element',
    COMBO = 'combo',
    PACKAGE = 'package',
    PHYSIO = 'physio',
}

const PHYSIO_PATTERN = /kin[eé]|physio/i;

function isPhysioPackage(billable: Billable): boolean {
    if (billable.type !== TYPE_BILL_ITEM.PACKAGE) return false;
    const concept = (billable.concept ?? '').toLowerCase();
    const category = typeof billable.category === 'string' ? billable.category.toLowerCase() : '';
    return PHYSIO_PATTERN.test(concept) || PHYSIO_PATTERN.test(category);
}

interface BillItemAddActionBarProps extends LocalizeContextProps {
    disabled?: boolean;
}

export const BillItemAddActionBar: React.FC<BillItemAddActionBarProps> = ({ translate, disabled }) => {
    const dispatch = useDispatch();
    const billables = useSelector((state: ReduxStore) => state.billing.data.billablesCurrentBill ?? []);
    const billableCombos = useSelector((state: ReduxStore) => state.billing.data.billableCombos ?? []);
    const loadingBillables = useSelector((state: ReduxStore) => state.billing.loading);
    const uuidInvestigation = useSelector((state: ReduxStore) => state.investigations.currentInvestigation?.uuid);
    const idBillingInfo = useSelector((state: ReduxStore) => state.investigations.currentInvestigation?.billingInfo?.id);

    const [addType, setAddType] = useState<BillItemAddType>(BillItemAddType.ELEMENT);
    const [selectedBillable, setSelectedBillable] = useState<Billable | null>(null);
    const [selectedCombo, setSelectedCombo] = useState<BillableCombo | null>(null);
    const [quantity, setQuantity] = useState(1);

    useEffect(() => {
        if (billableCombos.length === 0 && uuidInvestigation && idBillingInfo) {
            dispatch(getBillableComboAction(uuidInvestigation, idBillingInfo));
        }
    }, [dispatch, uuidInvestigation, idBillingInfo, billableCombos.length]);

    const packageBillables = useMemo(
        () => billables.filter((billable) => billable.type === TYPE_BILL_ITEM.PACKAGE && !isPhysioPackage(billable)),
        [billables],
    );

    const physioBillables = useMemo(
        () => billables.filter((billable) => isPhysioPackage(billable)),
        [billables],
    );

    // The element (prestation) catalogue is owned by PrestationSelector below. The Type
    // selector only needs to appear when there is something OTHER than plain elements to
    // add — combos, forfaits or physio sessions; otherwise the picker stands alone.
    const hasOtherTypes = billableCombos.length > 0 || packageBillables.length > 0 || physioBillables.length > 0;

    const resetSelection = () => {
        setSelectedBillable(null);
        setSelectedCombo(null);
        setQuantity(1);
    };

    // Derived, not corrected-after-render: when no other types exist the Type selector
    // is hidden, so ELEMENT is the only reachable mode regardless of stale `addType`
    // (e.g. combos vanished on a catalogue reload). This makes the empty-type state
    // unrepresentable instead of fixing it with an effect.
    const effectiveAddType = hasOtherTypes ? addType : BillItemAddType.ELEMENT;

    const handleTypeChange = (nextType: BillItemAddType) => {
        setAddType(nextType);
        resetSelection();
    };

    const handleAdd = () => {
        if (effectiveAddType === BillItemAddType.COMBO && selectedCombo) {
            dispatch(pushBillables(selectedCombo.billables));
            setSelectedCombo(null);
            return;
        }
        if (!selectedBillable) return;
        if (effectiveAddType === BillItemAddType.PACKAGE || effectiveAddType === BillItemAddType.PHYSIO) {
            const qty = Math.min(Math.max(quantity, 1), 50);
            dispatch(pushBillables(Array(qty).fill({ id: selectedBillable.id })));
        }
        resetSelection();
    };

    const selectorOptions =
        effectiveAddType === BillItemAddType.PACKAGE
            ? packageBillables
            : effectiveAddType === BillItemAddType.PHYSIO
              ? physioBillables
              : [];

    const canAdd =
        !disabled &&
        !loadingBillables &&
        ((effectiveAddType === BillItemAddType.COMBO && selectedCombo !== null) ||
            ((effectiveAddType === BillItemAddType.PACKAGE || effectiveAddType === BillItemAddType.PHYSIO) && selectedBillable !== null));

    const showQuantity = effectiveAddType === BillItemAddType.PACKAGE || effectiveAddType === BillItemAddType.PHYSIO;

    // SHER-460 #4: the Type selector is a derived node, rendered only when there is more than one
    // type to choose from. It shares a line with the field it drives — inline with the search box
    // for ELEMENT (PrestationSelector's typeSelector slot), or the first cell of the add row.
    const typeSelectorNode = hasOtherTypes ? (
        <FormControl fullWidth variant="outlined" size="small">
            <InputLabel id="bill-add-type-label">
                <Translate id="hospital.billing.bill.selectors.add_type" />
            </InputLabel>
            <Select
                labelId="bill-add-type-label"
                value={effectiveAddType}
                label={translate('hospital.billing.bill.selectors.add_type').toString()}
                onChange={(event) => handleTypeChange(event.target.value as BillItemAddType)}
                disabled={disabled}
            >
                <MenuItem value={BillItemAddType.ELEMENT}>
                    <Translate id="hospital.billing.bill.selectors.type_element" />
                </MenuItem>
                {billableCombos.length > 0 && (
                    <MenuItem value={BillItemAddType.COMBO}>
                        <Translate id="hospital.billing.bill.selectors.type_combo" />
                    </MenuItem>
                )}
                {packageBillables.length > 0 && (
                    <MenuItem value={BillItemAddType.PACKAGE}>
                        <Translate id="hospital.billing.bill.selectors.type_forfait" />
                    </MenuItem>
                )}
                {physioBillables.length > 0 && (
                    <MenuItem value={BillItemAddType.PHYSIO}>
                        <Translate id="hospital.billing.bill.selectors.type_physio_session" />
                    </MenuItem>
                )}
            </Select>
        </FormControl>
    ) : null;
    const selectorMd = showQuantity ? (typeSelectorNode ? 5 : 8) : (typeSelectorNode ? 7 : 10);

    if (loadingBillables) {
        return null;
    }

    return (
        <Box
            data-testid="bill-item-add-action-bar"
            sx={{
                width: '100%',
                border: '1px solid #ddd',
                borderRadius: 1,
                p: 2,
                mb: 2,
                backgroundColor: '#fafafa',
            }}
        >
            <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                <Translate id="hospital.billing.bill.selectors.add_item_title" />
            </Typography>

            {effectiveAddType === BillItemAddType.ELEMENT ? (
                <PrestationSelector disabled={disabled} typeSelector={typeSelectorNode ?? undefined} />
            ) : (
                <Grid container spacing={2} alignItems="center">
                    {typeSelectorNode && (
                        <Grid item xs={12} md={3}>
                            {typeSelectorNode}
                        </Grid>
                    )}
                    <Grid item xs={12} md={selectorMd}>
                        {effectiveAddType === BillItemAddType.COMBO ? (
                            <Autocomplete
                                value={selectedCombo}
                                options={billableCombos}
                                onChange={(_, value) => setSelectedCombo(value)}
                                getOptionLabel={(option) => option.name}
                                disabled={disabled}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        label={<Translate id="hospital.billing.bill.selectors.select_combo" />}
                                        variant="outlined"
                                    />
                                )}
                            />
                        ) : (
                            <Autocomplete
                                value={selectedBillable}
                                options={selectorOptions as Billable[]}
                                onChange={(_, value) => setSelectedBillable(value)}
                                getOptionLabel={(option) => option.concept ?? ''}
                                disabled={disabled || selectorOptions.length === 0}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        label={<Translate id="hospital.billing.bill.selectors.select_prestation" />}
                                        variant="outlined"
                                    />
                                )}
                            />
                        )}
                    </Grid>

                    {showQuantity && (
                        <Grid item xs={6} md={2}>
                            <TextField
                                type="number"
                                size="small"
                                fullWidth
                                label={<Translate id="hospital.billing.bill.selectors.quantity" />}
                                value={quantity}
                                onChange={(event) => setQuantity(parseInt(event.target.value, 10) || 1)}
                                inputProps={{ min: 1, max: 50 }}
                                disabled={disabled}
                                variant="outlined"
                            />
                        </Grid>
                    )}

                    <Grid item xs={6} md={2}>
                        <ButtonAdd
                            data-testid="unified-add-bill-item"
                            onClick={handleAdd}
                            disabled={!canAdd}
                        />
                    </Grid>
                </Grid>
            )}

            <Typography variant="caption" color="textSecondary" display="block" mt={1}>
                <Translate id="hospital.billing.bill.selectors.add_item_helper" />
            </Typography>
        </Box>
    );
};

export default withLocalize(BillItemAddActionBar);
